/**
 * tldv Meeting Picker Component
 * Allows users to browse and select meetings to sync transcripts from
 */

import { useState, useEffect } from 'react';
import {
  Check,
  X,
  Loader2,
  Search,
  Video,
} from 'lucide-react';
import { api, type TldvMeeting } from '../lib/api';

interface TldvMeetingPickerProps {
  onClose: () => void;
  onSave: (selectedMeetings: TldvMeeting[]) => void;
  initialSelected?: TldvMeeting[];
}

export function TldvMeetingPicker({ onClose, onSave, initialSelected = [] }: TldvMeetingPickerProps) {
  const [meetings, setMeetings] = useState<TldvMeeting[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [selectedById, setSelectedById] = useState<Record<string, TldvMeeting>>(() => {
    const initial: Record<string, TldvMeeting> = {};
    for (const meeting of initialSelected) {
      initial[meeting.id] = meeting;
    }
    return initial;
  });

  useEffect(() => {
    loadMeetings();
  }, [page, searchQuery]);

  const loadMeetings = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await api.listTldvMeetings({
        page,
        limit: 20,
        query: searchQuery || undefined,
      });
      if (result.data?.data) {
        setMeetings(result.data.data.results);
        setTotalPages(result.data.data.pages);
      } else if (result.error) {
        setError(result.error.message);
      }
    } catch {
      setError('Failed to load meetings');
    }
    setLoading(false);
  };

  const toggleMeeting = (meeting: TldvMeeting) => {
    setSelectedById(prev => {
      if (meeting.id in prev) {
        const { [meeting.id]: _, ...rest } = prev;
        return rest;
      } else {
        return { ...prev, [meeting.id]: meeting };
      }
    });
  };

  const handleSave = () => {
    onSave(Object.values(selectedById));
  };

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    if (mins < 60) return `${mins}m`;
    const hrs = Math.floor(mins / 60);
    const remainMins = mins % 60;
    return `${hrs}h ${remainMins}m`;
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const selectedCount = Object.keys(selectedById).length;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="p-4 border-b flex items-center justify-between">
          <h2 className="text-lg font-semibold">Select Meetings</h2>
          <button
            onClick={onClose}
            className="p-1 text-gray-500 hover:text-gray-700 rounded"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Search */}
        <div className="px-4 py-3 border-b">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setPage(1);
              }}
              placeholder="Search meetings..."
              className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-mazo-500 focus:border-mazo-500"
            />
          </div>
        </div>

        {/* Meeting List */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-mazo-600" />
            </div>
          ) : error ? (
            <div className="p-4 text-center text-red-600">{error}</div>
          ) : meetings.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <Video className="h-12 w-12 mx-auto mb-2 text-gray-300" />
              <p>No meetings found</p>
            </div>
          ) : (
            <div className="divide-y">
              {meetings.map((meeting) => {
                const isSelected = meeting.id in selectedById;
                return (
                  <button
                    type="button"
                    key={meeting.id}
                    onClick={() => toggleMeeting(meeting)}
                    className="w-full px-4 py-3 flex items-center gap-3 text-left hover:bg-gray-50 transition-colors"
                  >
                    <div
                      className={`flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                        isSelected
                          ? 'bg-mazo-600 border-mazo-600 text-white'
                          : 'border-gray-300'
                      }`}
                    >
                      {isSelected && <Check className="h-3 w-3" />}
                    </div>
                    <Video className="h-5 w-5 text-red-400 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="truncate font-medium text-gray-900">{meeting.name}</div>
                      <div className="text-xs text-gray-500 flex items-center gap-2">
                        <span>{formatDate(meeting.happenedAt)}</span>
                        <span className="text-gray-300">|</span>
                        <span>{formatDuration(meeting.duration)}</span>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-4 py-2 border-t bg-gray-50 flex items-center justify-center gap-2">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="px-3 py-1 text-sm border rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <span className="text-sm text-gray-600">
              Page {page} of {totalPages}
            </span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="px-3 py-1 text-sm border rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        )}

        {/* Footer */}
        <div className="p-4 border-t bg-gray-50 flex items-center justify-between">
          <div className="text-sm text-gray-600">
            {selectedCount} meeting{selectedCount !== 1 ? 's' : ''} selected
          </div>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 border rounded-lg hover:bg-gray-100"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={selectedCount === 0}
              className="px-4 py-2 bg-mazo-600 text-white rounded-lg hover:bg-mazo-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Save Selection
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
